import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { MessageSquare, FileText } from "lucide-react-native";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import {
  QuestionLayout,
  IconInput,
} from "@src/packages/medical-history/components";
import { MoonDateField } from "@louise/moon-ui";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import { useSurgeries } from "../SurgeriesContext";
import type { SurgeryDetail } from "../SurgeriesContext";
import type { SurgeriesStackParamList } from "../SurgeriesNavigator";
import { getSurgeryLabelKey } from "../options";
import type { SurgeryType } from "../options";
import { useMedicalHistoryTheme } from "../../../theme/MedicalHistoryTheme";

export function SurgeriesDetailsScreen() {
  const t = useTranslations();
  const { state, updateState } = useSurgeries();
  const navigation =
    useNavigation<NativeStackNavigationProp<SurgeriesStackParamList>>();
  const theme = useMedicalHistoryTheme();

  // Group surgeries by type, preserving order
  const groupedSurgeries = useMemo(() => {
    const groups: {
      surgeryType: SurgeryType;
      labelKey: string;
      isOther: boolean;
      entries: { entry: SurgeryDetail; globalIndex: number }[];
    }[] = [];
    const seenTypes = new Map<string, number>();

    state.surgeries.forEach((entry, globalIndex) => {
      const existing = seenTypes.get(entry.surgery);
      if (existing !== undefined) {
        groups[existing]!.entries.push({ entry, globalIndex });
      } else {
        seenTypes.set(entry.surgery, groups.length);
        groups.push({
          surgeryType: entry.surgery,
          labelKey: getSurgeryLabelKey(entry.surgery),
          isOther: entry.surgery === "other",
          entries: [{ entry, globalIndex }],
        });
      }
    });

    return groups;
  }, [state.surgeries]);

  const updateSurgery = (index: number, updates: Partial<SurgeryDetail>) => {
    const updated = state.surgeries.map((s, i) =>
      i === index ? { ...s, ...updates } : s,
    );
    updateState({ surgeries: updated });
  };

  const handleSave = () => {
    navigation.navigate("SurgeriesComplete");
  };

  return (
    <QuestionLayout
      title={t("med_history.medical.surgeries.details_title")}
      subtitle={t("med_history.medical.surgeries.details_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={false}
      saveLabel={t("med_history.medical.shared.next")}
    >
      <View style={styles.container}>
        {groupedSurgeries.map((group) => (
          <View key={group.surgeryType}>
            {group.entries.map(({ entry, globalIndex }) => (
              <View
                key={globalIndex}
                style={[GLASS_CARD_STYLE, styles.detailCard]}
              >
                <View style={styles.cardHeader}>
                  <Text style={[styles.cardTitle, { color: theme.text }]}>
                    {group.isOther
                      ? t("med_history.medical.surgeries.other_card_label")
                      : t("med_history.medical.surgeries.card_label", {
                          params: {
                            label: t(group.labelKey),
                          },
                        })}
                  </Text>
                </View>

                {group.isOther && (
                  <IconInput
                    icon={FileText}
                    label={t("med_history.medical.surgeries.other_name_label")}
                    placeholder={t(
                      "med_history.medical.surgeries.other_name_placeholder",
                    )}
                    value={entry.other ?? ""}
                    onChangeText={(text) =>
                      updateSurgery(globalIndex, {
                        other: text || null,
                      })
                    }
                  />
                )}

                <MoonDateField
                  label={t(
                    "med_history.medical.surgeries.procedure_date_label",
                  )}
                  placeholder={t(
                    "med_history.medical.surgeries.procedure_date_placeholder",
                  )}
                  value={entry.date ?? ""}
                  onChange={(iso) =>
                    updateSurgery(globalIndex, { date: iso || null })
                  }
                />

                <IconInput
                  icon={MessageSquare}
                  label={t("med_history.medical.surgeries.comments_label")}
                  placeholder={t(
                    "med_history.medical.surgeries.comments_placeholder",
                  )}
                  value={entry.comments ?? ""}
                  onChangeText={(text) =>
                    updateSurgery(globalIndex, {
                      comments: text || null,
                    })
                  }
                  multiline
                  numberOfLines={3}
                />
              </View>
            ))}
          </View>
        ))}
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
    gap: 16,
  },
  detailCard: {
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    gap: 16,
    marginBottom: 16,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: "600",
  },
});
